import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useFormContext } from "react-hook-form";
import formatDate from '../../../../Utilities/FormateDayjsToDateOnly';
import monthDropdown from '../../../../Utilities/Constants/MonthDropdown';
import ShiftPatternDropdown from '../../../../Utilities/Constants/ShifttPatternDropdown';
import yesNoDropdown from '../../../../Utilities/Constants/YesNoDropdown';
import DataContainer from '../../../../Utilities/DataContainer';
import RenderSelectField from '../../../../Utilities/Forms/RenderSelectField';
import RenderTextField from '../../../../Utilities/Forms/RenderTextField';
import ValidatedSelectElement from '../../../../Utilities/Forms/ValidatedSelectElement';
import ValidatedTextField from '../../../../Utilities/Forms/ValidatedTextField';
import DropDownViewModel from '../../../../Utilities/ViewModels/DropDownViewModel';


interface IProps {
    planTypes: DropDownViewModel[] | null
    isReadOnly: boolean
}


export default function TravelPlanMainDetailsV2({ planTypes, isReadOnly }: IProps) {

    const { getValues, setValue, watch } = useFormContext();
    const formValues = getValues();
    const surveyYear = watch('surveyYear');
    const prevSurveyYear = watch('prevSurveyYear');
    const hasValues = (fields: string[]) => fields.some(field => formValues[field]) || !isReadOnly;

    const handleDateChange = (date: Dayjs | null, field: 'surveyYear' | 'prevSurveyYear') => {
        setValue(field, date ? formatDate(date) : null, {
            shouldDirty: true,
            shouldValidate: true
        });
    };

    return (
        <>
            <Grid container alignItems="flex-start">
                <Grid size={4} container>
                    {(hasValues(["planType", "bondCondition"])) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Travel Plan Type"}>
                                    <Grid size={12}>
                                        <Stack spacing={1}>
                                            <RenderSelectField label="Type" name="planType" options={planTypes} readOnly={isReadOnly} />
                                            <RenderSelectField label="Has the local authority placed a 'bond' condition to ensure Travel Plan targets are met?" name="bondCondition" options={yesNoDropdown} readOnly={isReadOnly} />
                                        </Stack>
                                    </Grid>
                                </DataContainer>
                            </Grid>
                        </Grid>
                    )}

                    {(hasValues(["hasCoordinator", "coordinatorTime", "annualBudget"]) || !isReadOnly) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Travel Plan Coordination"}>
                                    <RenderSelectField label="Is there a Travel Plan co-ordinator - a member of staff whose job it is to manage the implementation of the Travel Plan" name="hasCoordinator" options={yesNoDropdown} readOnly={isReadOnly} />
                                    <RenderSelectField label="If YES to the above, do they work on the Travel Plan full time or part time?" name="coordinatorTime" options={ShiftPatternDropdown} readOnly={isReadOnly} />
                                    <RenderSelectField label="Is there an annual Travel Plan budget allocated for this site?" name="annualBudget" options={yesNoDropdown} readOnly={isReadOnly} />
                                </DataContainer>
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                <Grid size={4} container>
                    {(hasValues(["initialPlanMonth", "initialPlanMonth", "currentPlanMonth", "currentPlanYear", "planAccredited", "accreditingOrg", "surveyYear", "prevSurveyYear", "nonTRICSSurveys"]) || !isReadOnly) && (
                        <Grid container size={12}>
                            <Grid size={12}>
                                <DataContainer title={"Travel Plan History"}>
                                    <Grid container size={12}>
                                        <Grid size={12}>
                                            <Typography variant={"body1"}>Date of initial travel plan implementation</Typography>
                                        </Grid>
                                        <Stack direction="row" spacing={1}>
                                            <Grid size={6}>
                                                <ValidatedSelectElement name={'initialPlanMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                            </Grid>
                                            <Grid size={6}>
                                                <ValidatedTextField name={'initialPlanYear'} />
                                            </Grid>
                                        </Stack>
                                    </Grid>
                                </DataContainer>
                            </Grid>

                            <Grid container size={12}>
                                <Grid size={12}>
                                    <Typography variant={"body1"}>Date of current travel plan implementation</Typography>
                                </Grid>
                                <Stack direction="row" spacing={1}>
                                    <Grid size={6}>
                                        <ValidatedSelectElement name={'currentPlanMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                    </Grid>
                                    <Grid size={6}>
                                        <ValidatedTextField name={'currentPlanYear'} />
                                    </Grid>
                                </Stack>
                            </Grid>
                            <Stack spacing={1}>
                                <RenderSelectField label="Has the Travel Plan been accredited by a recognised organisation?" name="planAccredited" options={yesNoDropdown} readOnly={isReadOnly} />
                                <RenderTextField label="Name of accrediting organisation" name="accreditingOrg" readOnly={isReadOnly} />
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                    <DatePicker
                                        label="Date of this survey"
                                        value={surveyYear ? dayjs(surveyYear) : null}
                                        onChange={(date) => handleDateChange(date, 'surveyYear')}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { size: 'small' } }}
                                        readOnly={isReadOnly}
                                    />

                                    <DatePicker
                                        label="Date of previous survey (if applicable)"
                                        value={prevSurveyYear ? dayjs(prevSurveyYear) : null}
                                        onChange={(date) => handleDateChange(date, 'prevSurveyYear')}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { size: 'small' } }}
                                        readOnly={isReadOnly}
                                    />
                                </LocalizationProvider>
                                <RenderSelectField label="Have any other non-TRICS surveys been undertaken at the site for Travel Plan purposes?" name="nonTRICSSurveys" options={yesNoDropdown} readOnly={isReadOnly} />
                                <RenderTextField label="Detail of survey undertaken" name="nonTRICSSurveysDetails" readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} />
                            </Stack>
                        </Grid>
                    )}
                </Grid>

                <Grid size={4} container>
                    <Grid container size={12}>
                        <Grid size={12}>
                            <Typography variant="h6">Travel Plan Developer Initiatives</Typography>
                        </Grid>
                        <Grid size={12}>
                            <RenderTextField label="List any developer initiatives (as per planning agreements: for example local infrastructure, improvements to local public transport services, etc.)" name="devInitiatives1" readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} alwaysShow={true} />

                        </Grid>
                    </Grid>

                    <Grid container size={12}>
                        <Grid size={12}>
                            <Typography variant={"h6"}>Changes from Previous Survey</Typography>
                        </Grid>
                        <RenderTextField name="devInitiatives2" readOnly={isReadOnly} multiLine={true} rows={4} label={''} labelSize={12} inputSize={12} alwaysShow={true} />
                    </Grid>
                </Grid>
            </Grid >
        </>
    );
}
